import { useCallback, useState } from "react";

import { useChartDataTableContextMenu } from "./useChartDataTableContextMenu";
import { useChartDataTableCopy } from "./useChartDataTableCopy";

export enum ContextMenuAction {
  CopyCell = "copyCell",
  CopyRow = "copyRow",
  CopyColumn = "copyColumn"
}

interface UseChartDataTableInteractionsOptions {
  rows: Record<string, unknown>[];
  columns: Array<{ name: string; key: string }>;
  hasTrialAccount: boolean;
  isReadonly: boolean;
}

export function useChartDataTableInteractions({
  rows,
  columns,
  hasTrialAccount,
  isReadonly
}: UseChartDataTableInteractionsOptions) {
  const [highlightedCell, setHighlightedCell] = useState<{
    rowIndex: number;
    columnKey: string;
  } | null>(null);

  const {
    copyCellContents,
    copyRowContents,
    copyColumnData,
    copyTableData,
    showCopySuccessMessage: activeColumnCopyAlerts
  } = useChartDataTableCopy({ rows, columns });

  const { contextMenu, setContextMenu, handleContextMenuClose } =
    useChartDataTableContextMenu();

  const handleCellClick = useCallback(
    (rowIdx: number, row: Record<string, unknown>, column: { key: string }) => {
      if (hasTrialAccount || isReadonly) {
        return;
      }

      const cellValue = row[column.key];

      if (cellValue != null) {
        copyCellContents(cellValue as string | number);
        setHighlightedCell({ rowIndex: rowIdx, columnKey: column.key });
        setTimeout(() => setHighlightedCell(null), 1000);
      }
    },
    [hasTrialAccount, isReadonly, copyCellContents]
  );

  const handleContextMenuAction = useCallback(
    (action: ContextMenuAction) => {
      if (!contextMenu) return;

      switch (action) {
        case ContextMenuAction.CopyRow:
          copyRowContents(contextMenu.row);
          break;
        case ContextMenuAction.CopyCell:
          if (contextMenu.cellValue != null) {
            copyCellContents(contextMenu.cellValue);
          }
          break;
        case ContextMenuAction.CopyColumn:
          if (contextMenu.columnKey && contextMenu.columnName) {
            copyColumnData(contextMenu.columnName, contextMenu.columnKey);
          }
          break;
      }

      handleContextMenuClose();
    },
    [
      contextMenu,
      copyRowContents,
      copyCellContents,
      copyColumnData,
      handleContextMenuClose
    ]
  );

  return {
    // State
    highlightedCell,
    contextMenu,
    activeColumnCopyAlerts,

    // Actions
    handleCellClick,
    handleContextMenuAction,
    copyColumnData,
    copyTableData,
    setContextMenu
  };
}
